'use client'

import * as React from 'react'
import { Slider as SliderPrimitive } from '@base-ui/react/slider'

import cn from '@config/tailwind/cn'

import { typographyVariants } from '../lib/typography'

function valueToPercent(raw: number, min: number, max: number) {
  if (max <= min) return 0
  return ((raw - min) / (max - min)) * 100
}

interface SliderRangeProps {
  value: [number, number]
  onValueChange: (value: [number, number]) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  className?: string
  /** Renders each endpoint value under its thumb, moving with the value. */
  showThumbValueLabels?: boolean
  thumbValueLabelClassName?: string
}

function SliderRange({
  value,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  disabled,
  className,
  showThumbValueLabels = false,
  thumbValueLabelClassName,
}: SliderRangeProps) {
  return (
    <SliderPrimitive.Root
      value={value}
      onValueChange={(newValue) => {
        if (Array.isArray(newValue) && newValue.length === 2) {
          onValueChange([newValue[0] as number, newValue[1] as number])
        }
      }}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      className={cn('ui:relative ui:flex ui:w-full ui:flex-col ui:gap-1 ui:touch-none ui:select-none', className)}
    >
      <SliderPrimitive.Control className='ui:relative ui:flex ui:w-full ui:items-center'>
        <SliderPrimitive.Track className='ui:relative ui:h-1.5 ui:w-full ui:grow ui:overflow-hidden ui:rounded-full ui:bg-grey-30'>
          <SliderPrimitive.Indicator className='ui:absolute ui:h-full ui:bg-blue-100' />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb className='ui:block ui:size-4 ui:rounded-full ui:bg-blue-100 ui:shadow ui:transition-colors ui:focus-visible:outline-none ui:focus-visible:ring-1 ui:focus-visible:ring-blue-50 ui:disabled:pointer-events-none ui:disabled:opacity-50' />
        <SliderPrimitive.Thumb className='ui:block ui:size-4 ui:rounded-full ui:bg-blue-100 ui:shadow ui:transition-colors ui:focus-visible:outline-none ui:focus-visible:ring-1 ui:focus-visible:ring-blue-50 ui:disabled:pointer-events-none ui:disabled:opacity-50' />
      </SliderPrimitive.Control>
      {showThumbValueLabels ? (
        <div className='ui:pointer-events-none ui:relative ui:min-h-5 ui:w-full'>
          {value.map((v, index) => (
            <span
              key={index}
              className={cn(
                typographyVariants({ variant: 'body-sm' }),
                'ui:absolute ui:top-0 ui:-translate-x-1/2 ui:text-neutral-black ui:tabular-nums ui:transition-[left] ui:duration-75 ui:ease-out',
                thumbValueLabelClassName,
              )}
              style={{ left: `${String(valueToPercent(v, min, max))}%` }}
            >
              {String(v)}
            </span>
          ))}
        </div>
      ) : null}
    </SliderPrimitive.Root>
  )
}

export { SliderRange }
